import React, { useState, useEffect, useRef } from 'react';
import {
  X, Save, Trash2, AlertCircle, CheckCircle,
  RefreshCw, Upload, FolderOpen, ArrowLeft, File, Folder, Cloud
} from 'lucide-react';
import {
  Connector,
  CloudFile,
  ConnectorScope,
  ConnectorImportMode,
  ImportJob,
  apiService,
  getApiErrorMessage,
} from '../services/api';
import { generateIdempotencyKey } from '../services/idempotency';
import { useTabActivity } from '../contexts/TabActivityContext';
import './ConnectorDetailSidebar.css';

type ProviderType = 'gcs' | 's3' | 'azure';

interface CreateConnectorForm {
  name: string;
  provider: ProviderType;
  bucket: string;
  gcp_project_id: string;
  credentialsFile: File | null;
  region: string;
  aws_access_key_id: string;
  aws_secret_access_key: string;
  storage_account: string;
  container: string;
  tenant_id: string;
  client_id: string;
  client_secret: string;
  scopes: ConnectorScope[];
  import_mode: ConnectorImportMode;
  export_base_path: string;
}

const initialFormState: CreateConnectorForm = {
  name: '',
  provider: 'gcs',
  bucket: '',
  gcp_project_id: '',
  credentialsFile: null,
  region: '',
  aws_access_key_id: '',
  aws_secret_access_key: '',
  storage_account: '',
  container: '',
  tenant_id: '',
  client_id: '',
  client_secret: '',
  scopes: ['import'],
  import_mode: 'new_only',
  export_base_path: '',
};

const TERMINAL_CONNECTOR_CREATE_IDEMPOTENCY_CODES = new Set([
  'connector_creation_idempotency_terminal',
  'connector_creation_result_unavailable',
  'idempotency_key_reused',
]);

const shouldDiscardConnectorCreateAttemptKey = (error: unknown): boolean => {
  const response = (
    error as {
      response?: {
        status?: unknown;
        data?: {
          error?: {
            code?: unknown;
          };
        };
      };
    }
  )?.response;
  const status = response?.status;
  const code = response?.data?.error?.code;
  return (
    typeof status === 'number'
    && status >= 400
    && status < 500
    && typeof code === 'string'
    && TERMINAL_CONNECTOR_CREATE_IDEMPOTENCY_CODES.has(code)
  );
};

interface ConnectorDetailSidebarProps {
  connector: Connector | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (connector: Connector) => void;
  onDelete?: (connectorId: string) => void;
  isCreating?: boolean;
}

const ConnectorDetailSidebar: React.FC<ConnectorDetailSidebarProps> = ({
  connector,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isCreating = false
}) => {
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('connectorSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });
  const { isActive } = useTabActivity();
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreating);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [isUpdatingImportMode, setIsUpdatingImportMode] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateConnectorForm>(initialFormState);
  const [importModeDraft, setImportModeDraft] = useState<ConnectorImportMode>('all');

  // Browse state
  const [showBrowsePanel, setShowBrowsePanel] = useState(false);
  const [browseFiles, setBrowseFiles] = useState<CloudFile[]>([]);
  const [browsePath, setBrowsePath] = useState('');

  // Import jobs
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  // Test logs
  const [testLogs, setTestLogs] = useState<{ timestamp: string; success: boolean; message: string } | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  const connectorCreateAttemptKeyRef = useRef<string | null>(null);
  const connectorLifecycleLocked = Boolean(
    connector
    && ['testing', 'provisioning', 'deleting', 'cleanup_pending'].includes(
      connector.status,
    )
  );

  // A response-loss retry of unchanged form data must reuse the exact backend
  // operation identity. Any user edit starts a genuinely new request intent.
  useEffect(() => {
    connectorCreateAttemptKeyRef.current = null;
  }, [formData]);

  // Initialize form from connector
  useEffect(() => {
    if (connector && !isCreating) {
      setFormData({
        name: connector.name || '',
        provider: (connector.provider as ProviderType) || 'gcs',
        bucket: connector.bucket || '',
        gcp_project_id: connector.gcp_project_id || '',
        credentialsFile: null,
        region: connector.region || '',
        aws_access_key_id: '',
        aws_secret_access_key: '',
        storage_account: connector.storage_account || '',
        container: connector.container || '',
        tenant_id: '',
        client_id: '',
        client_secret: '',
        scopes: connector.scopes && connector.scopes.length > 0 ? connector.scopes : ['import'],
        import_mode: connector.import_mode || 'all',
        export_base_path: connector.export_base_path || '',
      });
      setImportModeDraft(connector.import_mode || 'all');
      setIsEditing(false);
      loadImportJobs(connector.connector_id);
    } else if (isCreating) {
      setFormData(initialFormState);
      setImportModeDraft(initialFormState.import_mode);
      setIsEditing(true);
      setImportJobs([]);
    }
  }, [connector, isCreating]);

  // Load import jobs
  const loadImportJobs = async (connectorId: string) => {
    try {
      setIsLoadingJobs(true);
      const jobs = await apiService.getImportJobsByConnector(connectorId);
      setImportJobs(jobs);
    } catch (error) {
      console.error('Failed to load import jobs:', error);
      setImportJobs([]);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isEditing && !showBrowsePanel) {
        onClose();
      }
    };

    if (isOpen && isActive) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isActive, isOpen, isEditing, showBrowsePanel, onClose]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem('connectorSidebarWidth', sidebarWidth.toString());
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('connectorSidebarWidthChange', { detail: { width: sidebarWidth } }));
    }
  }, [sidebarWidth, isOpen]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isActive || !isOpen) {
      setIsResizing(false);
      return;
    }
    if (!isResizing) return;
    document.body.classList.add('resizing-connector-sidebar');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(400, Math.min(1400, resizeStartWidth.current + deltaX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-connector-sidebar');
    };
  }, [isActive, isOpen, isResizing]);

  // File input change handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, credentialsFile: file });
    }
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Connector name is required';
    if (!formData.scopes || formData.scopes.length === 0) return 'At least one connector scope is required';
    if (formData.scopes.includes('export') && !formData.export_base_path.trim()) {
      return 'Export base path is required when export scope is enabled';
    }

    if (formData.provider === 'gcs') {
      if (!formData.bucket.trim()) return 'Bucket name is required';
      if (!formData.gcp_project_id.trim()) return 'GCP Project ID is required';
      if (isCreating && !formData.credentialsFile) return 'Service account key file is required';
    } else if (formData.provider === 's3') {
      if (!formData.bucket.trim()) return 'Bucket name is required';
      if (!formData.region.trim()) return 'Region is required';
      if (isCreating) {
        if (!formData.aws_access_key_id.trim()) return 'AWS Access Key ID is required';
        if (!formData.aws_secret_access_key.trim()) return 'AWS Secret Access Key is required';
      }
    } else if (formData.provider === 'azure') {
      if (!formData.storage_account.trim()) return 'Storage Account is required';
      if (!formData.container.trim()) return 'Container name is required';
      if (isCreating) {
        if (!formData.tenant_id.trim()) return 'Tenant ID is required';
        if (!formData.client_id.trim()) return 'Client ID is required';
        if (!formData.client_secret.trim()) return 'Client Secret is required';
      }
    }

    return null;
  };

  const toggleScope = (scope: ConnectorScope) => {
    setFormData((prev) => {
      const hasScope = prev.scopes.includes(scope);
      const scopes = hasScope
        ? prev.scopes.filter((value) => value !== scope)
        : [...prev.scopes, scope];
      return {
        ...prev,
        scopes,
      };
    });
  };

  // Save connector
  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      let savedConnector: Connector;

      if (isCreating) {
        const createAttemptKey = (
          connectorCreateAttemptKeyRef.current
          || generateIdempotencyKey(`connector-create-${formData.provider}`)
        );
        connectorCreateAttemptKeyRef.current = createAttemptKey;
        if (formData.provider === 'gcs') {
          savedConnector = await apiService.createGCSConnector(
            {
              name: formData.name.trim(),
              bucket: formData.bucket.trim(),
              gcp_project_id: formData.gcp_project_id.trim(),
              scopes: formData.scopes,
              import_mode: formData.import_mode,
              export_base_path: formData.scopes.includes('export') ? formData.export_base_path.trim() : undefined,
            },
            formData.credentialsFile!,
            createAttemptKey,
          );
        } else if (formData.provider === 's3') {
          savedConnector = await apiService.createS3Connector({
            name: formData.name.trim(),
            bucket: formData.bucket.trim(),
            region: formData.region.trim(),
            aws_access_key_id: formData.aws_access_key_id.trim(),
            aws_secret_access_key: formData.aws_secret_access_key.trim(),
            scopes: formData.scopes,
            import_mode: formData.import_mode,
            export_base_path: formData.scopes.includes('export') ? formData.export_base_path.trim() : undefined,
          }, createAttemptKey);
        } else {
          savedConnector = await apiService.createAzureConnector({
            name: formData.name.trim(),
            storage_account: formData.storage_account.trim(),
            container: formData.container.trim(),
            tenant_id: formData.tenant_id.trim(),
            client_id: formData.client_id.trim(),
            client_secret: formData.client_secret.trim(),
            scopes: formData.scopes,
            import_mode: formData.import_mode,
            export_base_path: formData.scopes.includes('export') ? formData.export_base_path.trim() : undefined,
          }, createAttemptKey);
        }

        connectorCreateAttemptKeyRef.current = null;
        setMessage({ type: 'success', text: 'Connector created successfully!' });
        setIsEditing(false);
        if (onSave) {
          onSave(savedConnector);
        }
      }
    } catch (error: unknown) {
      console.error('Failed to save connector:', error);
      if (
        isCreating
        && shouldDiscardConnectorCreateAttemptKey(error)
      ) {
        connectorCreateAttemptKeyRef.current = null;
      }
      const errorMessage = getApiErrorMessage(error, 'Failed to save connector. Please try again.');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete connector
  const handleDelete = async () => {
    if (!connector) return;
    if (connectorLifecycleLocked) {
      setMessage({
        type: 'error',
        text: 'This connector is already changing state. Wait for it to finish before deleting it.',
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete connector "${connector.name}"? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await apiService.deleteConnector(connector.connector_id);
      setMessage({ type: 'success', text: 'Connector deleted successfully!' });
      if (onDelete) {
        onDelete(connector.connector_id);
      }
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error, 'Failed to delete connector.');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsDeleting(false);
    }
  };

  // Test connector
  const handleTest = async () => {
    if (!connector) return;
    if (connectorLifecycleLocked) {
      setMessage({
        type: 'error',
        text: 'This connector is already changing state. Wait for it to finish before testing it.',
      });
      return;
    }

    setIsTesting(true);
    setMessage(null);
    setTestLogs(null);

    try {
      const result = await apiService.testConnector(connector.connector_id);
      const logMessage = result.error_message || (result.success ? 'Connection successful' : 'Connection failed');

      setTestLogs({
        timestamp: new Date().toISOString(),
        success: result.success,
        message: logMessage
      });

      if (result.success) {
        setMessage({ type: 'success', text: 'Connection test successful!' });
      } else {
        setMessage({ type: 'error', text: `Connection test failed: ${result.error_message || 'Unknown error'}` });
      }

      // Reload connector to get updated status
      const updatedConnector = await apiService.getConnector(connector.connector_id);
      if (onSave) {
        onSave(updatedConnector);
      }
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error, 'Failed to test connection.');
      setMessage({ type: 'error', text: errorMessage });
      setTestLogs({
        timestamp: new Date().toISOString(),
        success: false,
        message: errorMessage
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Browse files
  const handleBrowseFiles = async (prefix: string = '') => {
    if (!connector) return;

    setIsBrowsing(true);
    setBrowsePath(prefix);

    try {
      const files = await apiService.browseConnectorFiles(connector.connector_id, {
        prefix,
        pattern: '*',
        recursive: false
      });
      setBrowseFiles(files);
      setShowBrowsePanel(true);
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error, 'Failed to browse files.');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsBrowsing(false);
    }
  };

  // Format bytes
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Get provider label
  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'gcs':
        return 'Google Cloud Storage';
      case 's3':
        return 'Amazon S3';
      case 'azure':
        return 'Azure Blob Storage';
      default:
        return provider;
    }
  };

  const getImportModeLabel = (importMode?: ConnectorImportMode) => {
    return importMode === 'new_only' ? 'New files only (dedupe)' : 'All matching files';
  };

  // Get status class
  const getJobStatusClass = (status: string) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'importing': return 'status-importing';
      case 'scanning': return 'status-scanning';
      case 'pending': return 'status-pending';
      case 'failed': return 'status-failed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const handleUpdateImportMode = async () => {
    if (!connector) return;
    if (connectorLifecycleLocked) {
      setMessage({
        type: 'error',
        text: 'This connector is already changing state. Wait for it to finish before updating it.',
      });
      return;
    }
    if (importModeDraft === (connector.import_mode || 'all')) return;

    try {
      setIsUpdatingImportMode(true);
      setMessage(null);
      const updatedConnector = await apiService.updateConnector(connector.connector_id, {
        import_mode: importModeDraft,
      });
      setMessage({ type: 'success', text: 'Connector import mode updated.' });
      setImportModeDraft(updatedConnector.import_mode || importModeDraft);
      if (onSave) {
        onSave(updatedConnector);
      }
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error, 'Failed to update import mode.');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsUpdatingImportMode(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`connector-detail-sidebar ${isOpen ? 'sidebar-open' : ''} ${isResizing ? 'resizing' : ''}`}
        ref={modalRef}
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Resize handle */}
        <div
          className={`resize-handle ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleResizeStart}
        />

        <div className="sidebar-header">
          <h2>{isCreating ? 'Create New Connector' : 'Connector Details'}</h2>
          <div className="header-actions">
            {!isCreating && !isEditing && connector && (
              <>
                <button
                  className="action-button"
                  onClick={handleTest}
                  disabled={isTesting || connectorLifecycleLocked}
                  title="Test Connection"
                >
                  {isTesting ? <RefreshCw size={16} className="spinning" /> : <CheckCircle size={16} />}
                </button>
                <button
                  className="action-button"
                  onClick={() => handleBrowseFiles()}
                  disabled={isBrowsing || connector.status !== 'active'}
                  title="Browse Files"
                >
                  {isBrowsing ? <RefreshCw size={16} className="spinning" /> : <FolderOpen size={16} />}
                </button>
                <button
                  className="action-button danger"
                  onClick={handleDelete}
                  disabled={isDeleting || connectorLifecycleLocked}
                  title="Delete Connector"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        <div className="sidebar-content">
          {/* Browse Panel */}
          {showBrowsePanel && connector && (
            <div className="browse-panel">
              <div className="browse-panel-header">
                <h3>
                  <FolderOpen size={18} />
                  Browse Files
                </h3>
                <button
                  className="close-panel-btn"
                  onClick={() => setShowBrowsePanel(false)}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="browse-path-bar">
                {browsePath && (
                  <button
                    className="back-button"
                    onClick={() => {
                      const parentPath = browsePath.split('/').slice(0, -2).join('/');
                      handleBrowseFiles(parentPath ? parentPath + '/' : '');
                    }}
                  >
                    <ArrowLeft size={14} />
                    Back
                  </button>
                )}
                <span className="current-path">/{browsePath || ''}</span>
              </div>

              <div className="browse-files-list">
                {isBrowsing ? (
                  <div className="loading-files">
                    <RefreshCw size={16} className="spinning" />
                    <span>Loading files...</span>
                  </div>
                ) : browseFiles.length === 0 ? (
                  <div className="no-files">
                    <File size={24} />
                    <p>No files found</p>
                  </div>
                ) : (
                  browseFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`file-item ${file.path.endsWith('/') ? 'folder' : 'file'}`}
                      onClick={() => {
                        if (file.path.endsWith('/')) {
                          handleBrowseFiles(file.path);
                        }
                      }}
                    >
                      <div className="file-icon">
                        {file.path.endsWith('/') ? <Folder size={16} /> : <File size={16} />}
                      </div>
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-meta">
                          {!file.path.endsWith('/') && <span>{formatBytes(file.size_bytes)}</span>}
                          <span>{file.extension || 'folder'}</span>
                        </span>
                      </div>
                      {!file.path.endsWith('/') && (
                        <span className="file-modified">
                          {new Date(file.last_modified).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="section">
            <h3>Basic Information</h3>

            <div className="form-group">
              <label>Name *</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Cloud Storage"
                />
              ) : (
                <div className="readonly-value">{connector?.name}</div>
              )}
            </div>

            <div className="form-group">
              <label>Cloud Provider *</label>
              {isEditing ? (
                <select
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value as ProviderType })}
                >
                  <option value="gcs">Google Cloud Storage</option>
                  <option value="s3">Amazon S3</option>
                  <option value="azure">Azure Blob Storage</option>
                </select>
              ) : (
                <div className="readonly-value">
                  <Cloud size={14} />
                  {getProviderLabel(connector?.provider || '')}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Scopes *</label>
              {isEditing ? (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.scopes.includes('import')}
                      onChange={() => toggleScope('import')}
                    />
                    <span>Import</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.scopes.includes('export')}
                      onChange={() => toggleScope('export')}
                    />
                    <span>Export</span>
                  </label>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {(connector?.scopes || ['import']).map((scope) => (
                    <span
                      key={scope}
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.15rem 0.5rem',
                        borderRadius: '999px',
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {(isEditing ? formData.scopes.includes('import') : (connector?.scopes || ['import']).includes('import')) && (
              <div className="form-group">
                <label>Import Mode</label>
                {isEditing ? (
                  <>
                    <select
                      value={formData.import_mode}
                      onChange={(e) => setFormData({ ...formData, import_mode: e.target.value as ConnectorImportMode })}
                    >
                      <option value="new_only">New files only (dedupe)</option>
                      <option value="all">All matching files</option>
                    </select>
                    <div className="helper-text">
                      Controls default dedupe behavior for manual imports and automations.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="readonly-value">{getImportModeLabel(connector?.import_mode || 'all')}</div>
                    <div className="inline-control-row">
                      <select
                        value={importModeDraft}
                        onChange={(e) => setImportModeDraft(e.target.value as ConnectorImportMode)}
                        disabled={isUpdatingImportMode || connectorLifecycleLocked}
                      >
                        <option value="new_only">New files only</option>
                        <option value="all">All matching files</option>
                      </select>
                      <button
                        type="button"
                        className="inline-save-button"
                        onClick={handleUpdateImportMode}
                        disabled={
                          isUpdatingImportMode
                          || connectorLifecycleLocked
                          || importModeDraft === (connector?.import_mode || 'all')
                        }
                      >
                        {isUpdatingImportMode ? (
                          <>
                            <RefreshCw size={14} className="spinning" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                    <div className="helper-text">
                      This becomes the default for new import jobs unless overridden.
                    </div>
                  </>
                )}
              </div>
            )}

            {(isEditing ? formData.scopes.includes('export') : !!connector?.export_base_path) && (
              <div className="form-group">
                <label>Export Base Path {isEditing ? '*' : ''}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.export_base_path}
                    onChange={(e) => setFormData({ ...formData, export_base_path: e.target.value })}
                    placeholder="e.g. exports/team-a"
                  />
                ) : (
                  <div className="readonly-value code">{connector?.export_base_path || '-'}</div>
                )}
              </div>
            )}

            {!isCreating && connector && (
              <div className="form-group">
                <label>Status</label>
                <div className={`readonly-value status-value ${connector.status}`}>
                  {connector.status === 'active' && <CheckCircle size={14} />}
                  {connector.status === 'failed' && <AlertCircle size={14} />}
                  {['testing', 'provisioning', 'deleting', 'cleanup_pending'].includes(
                    connector.status,
                  ) && <RefreshCw size={14} className="spinning" />}
                  <span>{connector.status}</span>
                </div>
              </div>
            )}
          </div>

          {/* Provider-Specific Fields */}
          <div className="section">
            <h3>Connection Settings</h3>

            {/* GCS Fields */}
            {(isEditing ? formData.provider === 'gcs' : connector?.provider === 'gcs') && (
              <>
                <div className="form-group">
                  <label>Bucket Name *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.bucket}
                      onChange={(e) => setFormData({ ...formData, bucket: e.target.value })}
                      placeholder="my-bucket"
                    />
                  ) : (
                    <div className="readonly-value code">{connector?.bucket}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>GCP Project ID *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.gcp_project_id}
                      onChange={(e) => setFormData({ ...formData, gcp_project_id: e.target.value })}
                      placeholder="my-project-id"
                    />
                  ) : (
                    <div className="readonly-value code">{connector?.gcp_project_id}</div>
                  )}
                </div>

                {isCreating && (
                  <div className="form-group">
                    <label>Service Account Key (JSON) *</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="file-select-button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={16} />
                      {formData.credentialsFile ? formData.credentialsFile.name : 'Choose file...'}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* S3 Fields */}
            {(isEditing ? formData.provider === 's3' : connector?.provider === 's3') && (
              <>
                <div className="form-group">
                  <label>Bucket Name *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.bucket}
                      onChange={(e) => setFormData({ ...formData, bucket: e.target.value })}
                      placeholder="my-s3-bucket"
                    />
                  ) : (
                    <div className="readonly-value code">{connector?.bucket}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Region *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      placeholder="us-east-1"
                    />
                  ) : (
                    <div className="readonly-value">{connector?.region}</div>
                  )}
                </div>

                {isCreating && (
                  <>
                    <div className="form-group">
                      <label>AWS Access Key ID *</label>
                      <input
                        type="text"
                        value={formData.aws_access_key_id}
                        onChange={(e) => setFormData({ ...formData, aws_access_key_id: e.target.value })}
                        placeholder="AKIAIOSFODNN7EXAMPLE"
                      />
                    </div>

                    <div className="form-group">
                      <label>AWS Secret Access Key *</label>
                      <input
                        type="password"
                        value={formData.aws_secret_access_key}
                        onChange={(e) => setFormData({ ...formData, aws_secret_access_key: e.target.value })}
                        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Azure Fields */}
            {(isEditing ? formData.provider === 'azure' : connector?.provider === 'azure') && (
              <>
                <div className="form-group">
                  <label>Storage Account Name *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.storage_account}
                      onChange={(e) => setFormData({ ...formData, storage_account: e.target.value })}
                      placeholder="mystorageaccount"
                    />
                  ) : (
                    <div className="readonly-value code">{connector?.storage_account}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Container Name *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.container}
                      onChange={(e) => setFormData({ ...formData, container: e.target.value })}
                      placeholder="my-container"
                    />
                  ) : (
                    <div className="readonly-value code">{connector?.container}</div>
                  )}
                </div>

                {isCreating && (
                  <>
                    <div className="form-group">
                      <label>Tenant ID *</label>
                      <input
                        type="text"
                        value={formData.tenant_id}
                        onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                    </div>

                    <div className="form-group">
                      <label>Client ID *</label>
                      <input
                        type="text"
                        value={formData.client_id}
                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                    </div>

                    <div className="form-group">
                      <label>Client Secret *</label>
                      <input
                        type="password"
                        value={formData.client_secret}
                        onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                        placeholder="Your client secret"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Test Logs */}
          {testLogs && (
            <div className="section">
              <h3>Test Results</h3>
              <div className={`test-result ${testLogs.success ? 'success' : 'error'}`}>
                <div className="test-result-header">
                  {testLogs.success ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                  <span>{testLogs.success ? 'Success' : 'Failed'}</span>
                  <span className="test-time">{formatDate(testLogs.timestamp)}</span>
                </div>
                <pre className="test-output">{testLogs.message}</pre>
              </div>
            </div>
          )}

          {/* Connector Info */}
          {!isCreating && connector && (
            <div className="section">
              <h3>Connector Info</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDate(connector.created_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last Updated</span>
                  <span className="info-value">{formatDate(connector.updated_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last Tested</span>
                  <span className="info-value">{formatDate(connector.last_tested_at)}</span>
                </div>
                {connector.last_test_error && (
                  <div className="info-item full-width">
                    <span className="info-label">Last Error</span>
                    <span className="info-value error">{connector.last_test_error}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Import Jobs */}
          {!isCreating && connector && (
            <div className="section">
              <div className="section-header-row">
                <h3>Import Jobs</h3>
                <button
                  className="refresh-btn"
                  onClick={() => loadImportJobs(connector.connector_id)}
                  disabled={isLoadingJobs}
                >
                  <RefreshCw size={14} className={isLoadingJobs ? 'spinning' : ''} />
                </button>
              </div>

              <div className="import-jobs-list">
                {isLoadingJobs ? (
                  <div className="loading-jobs">Loading import jobs...</div>
                ) : importJobs.length === 0 ? (
                  <div className="no-jobs">
                    <p>No import jobs for this connector</p>
                    <small>Use "Import from Cloud" in the Index tab to create import jobs</small>
                  </div>
                ) : (
                  importJobs.map(job => (
                    <div key={job.job_id} className="import-job-item">
                      <div className="job-header">
                        <span className={`job-status ${getJobStatusClass(job.status)}`}>
                          {job.status === 'completed' && <CheckCircle size={12} />}
                          {(job.status === 'importing' || job.status === 'scanning') && <RefreshCw size={12} className="spinning" />}
                          {job.status === 'pending' && <AlertCircle size={12} />}
                          {(job.status === 'failed' || job.status === 'cancelled') && <AlertCircle size={12} />}
                          {job.status}
                        </span>
                        <span className="job-time">{formatDate(job.created_at)}</span>
                      </div>
                      <div className="job-details">
                        <div className="job-detail">
                          <span className="detail-label">Source:</span>
                          <span className="detail-value">{job.source_prefix || '/'}{job.file_pattern !== '*' && ` (${job.file_pattern})`}</span>
                        </div>
                        <div className="job-detail">
                          <span className="detail-label">Progress:</span>
                          <span className="detail-value">
                            {job.progress.imported} imported, {job.progress.failed} failed, {job.progress.skipped} skipped
                            {job.progress.total_files > 0 && ` of ${job.progress.total_files} files`}
                          </span>
                        </div>
                        {job.progress.bytes_transferred > 0 && (
                          <div className="job-detail">
                            <span className="detail-label">Transferred:</span>
                            <span className="detail-value">{formatBytes(job.progress.bytes_transferred)}</span>
                          </div>
                        )}
                        {job.error_message && (
                          <div className="job-detail error">
                            <span className="detail-label">Error:</span>
                            <span className="detail-value">{job.error_message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div className="sidebar-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  if (isCreating) {
                    onClose();
                  } else {
                    setIsEditing(false);
                  }
                }}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save size={16} />
                    {isCreating ? 'Create Connector' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConnectorDetailSidebar;
